import React, { FC } from 'react';
import { useHistory } from 'react-router';
import { CellProps, Column } from 'react-table';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import API from 'core/Api';
import { formatDate } from 'utils/helpers/formatters';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import Badge from 'components/Badge';
import { FaEdit, FaRegEnvelope } from 'react-icons/fa';
import { useModal } from 'store/modal';
import { useTranslation } from 'react-i18next';
import { createErrorFromResponse } from 'core/Error';
import copyToClipboard from 'utils/helpers/copyToClipboard';
import { IUser } from '../types';

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { showModal } = useModal();

  const createUser = () => history.push('/users/create');

  const resendActivation = (id: string) =>
    API.post(`users/${id}/resend-activation-email`)
      .then(() => {
        showModal({
          type: 'success',
          content: 'Съобщението за активиране на акаунта беше изпратено успешно.',
        });
      })
      .catch(err => {
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        });
      });

  const columns: Array<Column<IUser>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Име',
      accessor: 'first_name',
      Cell: (cell: CellProps<IUser>) => {
        return <span className="text-break">{cell.value}</span>;
      },
    },
    {
      Header: 'Фамилия',
      accessor: 'last_name',
      Cell: (cell: CellProps<IUser>) => {
        return <span className="text-break">{cell.value}</span>;
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ value }) => {
        const emailPreview = value.length > 30 ? `${value.substr(0, 30)}...` : value;

        return (
          <TooltipWrapper
            id="tooltip-email"
            overlay={`${t('common.copyToClipboard')}: ${value}`}
          >
            <span onClick={() => copyToClipboard(value)}>{emailPreview}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: 'Дата на регистрация',
      accessor: 'created_at',
      Cell: cell => <>{formatDate(new Date(cell.row.original.created_at), 'dd.mm.Y hh:ii:ss')}</>,
    },

    {
      Header: 'Статус',
      accessor: 'account_status',
      disableSortBy: true,
      Cell: cell => {
        if (cell.row.original.account_status === 'active')
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: () => (
        <span>
          Двуфакторно
          <br /> автентикиране
        </span>
      ),
      accessor: 'two_fa_confirmed',
      disableSortBy: true,
      maxWidth: 80,
      width: 80,
      minWidth: 80,
      Cell: cell => {
        const is2FaActive = cell.row.original.two_fa_confirmed;

        if (is2FaActive)
          return (
            <Badge
              label="Активно"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивно"
            variant="danger"
          />
        );
      },
    },
    {
      Header: () => <div className="text-right">Действия</div>,
      accessor: 'actions',
      disableSortBy: true,
      Cell: cell => {
        const { id, account_activated: accountActivated } = cell.row.original;
        return (
          <div className="text-right">
            {!accountActivated && (
              <span className="mr-2">
                <TooltipWrapper
                  id="tooltip-resend"
                  overlay="Изпрати отново имейл за активация"
                >
                  <Button
                    btnType="secondary"
                    size="sm"
                    outlined
                    onClick={() => resendActivation(id)}
                  >
                    <FaRegEnvelope size={14} />
                  </Button>
                </TooltipWrapper>
              </span>
            )}
            <TooltipWrapper
              id="tooltip-edit"
              overlay="Редактирай"
            >
              <Button
                btnType="primary"
                size="sm"
                outlined
                onClick={() => history.push(`/users/edit/${cell.row.original.id}`)}
              >
                <FaEdit size={14} />
              </Button>
            </TooltipWrapper>
          </div>
        );
      },
    },
  ];

  return (
    <MainLayout title="Управление на оператори">
      <FetchTable<IUser>
        columns={columns}
        onAdd={createUser}
        addBtnLabel="Добавяне на оператор"
        entityName="users"
        entityEndpoint="users"
      />
    </MainLayout>
  );
};

export default Screen;
